import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { http, resolveImage, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, Edit3, Download, QrCode } from "lucide-react";

/* ─── Extracted admin sub-components ─── */
import ArtworkModal from "@/components/admin/AdminArtworkModal";
import OrderModal from "@/components/admin/AdminOrderModal";
import CustomerModal from "@/components/admin/AdminCustomerModal";
import ResetModal from "@/components/admin/AdminResetModal";
import LocationModal from "@/components/admin/AdminLocationModal";
import PlanModal from "@/components/admin/AdminPlanModal";
import MessagesPanel from "@/components/admin/AdminMessages";
import SubscriptionsPanel from "@/components/admin/AdminSubscriptions";
import PagesPanel from "@/components/admin/AdminPages";
import ContentCMSTab from "@/components/admin/AdminContentCMS";

/* ─── Pre-existing standalone admin components ─── */
import GiftCardsAdmin from "@/components/GiftCardsAdmin";
import PricingAdmin from "@/components/PricingAdmin";
import EmailTemplatesAdmin from "@/components/EmailTemplatesAdmin";
import CampaignsAdmin from "@/components/CampaignsAdmin";
import AdminReviews from "@/components/AdminReviews";
import AdminTruths from "@/components/AdminTruths";
import AdminTruthReleases from "@/components/AdminTruthReleases";
import AdminEngagement from "@/components/AdminEngagement";
import AdminInterpretations from "@/components/AdminInterpretations";
import ShippingAdmin from "@/components/ShippingAdmin";
import AdminBadges from "@/components/AdminBadges";
import AdminSupport from "@/components/AdminSupport";
import AdminTruthWall from "@/components/AdminTruthWall";
import AdminTruthMarkers from "@/components/AdminTruthMarkers";
import AdminTruthCards from "@/components/AdminTruthCards";
import AdminTruthPrint from "@/components/AdminTruthPrint";
import AdminTruthStand from "@/components/AdminTruthStand";
import AdminMirrorConversations from "@/components/AdminMirrorConversations";
import AdminMirrorLeads from "@/components/AdminMirrorLeads";
import AdminReflectionLibrary from "@/components/AdminReflectionLibrary";
import AdminBooking from "@/components/AdminBooking";
import AdminForms from "@/components/AdminForms";
import AdminClientMgmt from "@/components/AdminClientMgmt";
import { AdminEmptyChairSetup, AdminEmptyChairEpisodes, AdminEmptyChairRequests, AdminEmptyChairFAQ, AdminEmptyChairTestimonials, AdminEmptyChairInquiries, AdminEmptyChairExport } from "@/components/AdminEmptyChair";
import AdminBreathingRoom from "@/components/AdminBreathingRoom";
import AdminNavManager from "@/components/AdminNavManager";
import AdminWorkshops from "@/components/AdminWorkshops";
import AdminOffSiteSale from "@/components/AdminOffSiteSale";
import AdminRegistry from "@/components/AdminRegistry";
import AdminSEO from "@/components/AdminSEO";
import AdminGuestReleases from "@/components/AdminGuestReleases";
import GuestReleaseFormSettings from "@/components/GuestReleaseFormSettings";
import AdminPartnerProgram from "@/components/AdminPartnerProgram";
import AdminMarkerCollection from "@/components/AdminMarkerCollection";

/* ═══════════════════════════════════════════════════
   TAB GROUPS — describes the navigation structure
═══════════════════════════════════════════════════ */
const TAB_GROUPS = [
  { label: "Catalog", testid: "admin-group-catalog", tabs: [["artworks","Artworks"],["pricing","Pricing"],["pages","Pages"],["content","Site CMS"]] },
  { label: "Sales", testid: "admin-group-sales", tabs: [["orders","Orders"],["customers","Customers"],["gift-cards","Gift Cards"],["subscriptions","Subscriptions"],["plans","Plans"],["support","Support Mission"]] },
  { label: "Truth Registry", testid: "admin-group-truth", tabs: [["truths","Registry"],["registry-admin","Registry CRUD"],["releases","Releases"],["interpretations","Interpretations"],["truth-wall","Truth Wall"],["truth-markers","Truth Markers"],["truth-cards","Truth Cards"],["truth-print","Print Builder"],["offsite-sale","Off-Site Sale"],["qrcodes","QR Codes"],["locations","Locations"]] },
  { label: "Mirror Conversations", testid: "admin-group-mirror", tabs: [["mirror","Mirror Setup"],["mirror-contacts","Leads & Bookings"],["reflection-library","Reflection Library"],["booking","Booking System"],["forms","Agreements & Forms"],["client-mgmt","Client Management"],["breathing-room","Breathing Room"],["workshops-admin","Workshops"]] },
  { label: "The Empty Chair", testid: "admin-group-empty-chair", tabs: [["empty-chair","Podcast Setup"],["empty-chair-episodes","Episodes"],["empty-chair-requests","Guest Requests"],["empty-chair-faq","Podcast FAQ"],["empty-chair-testimonials","Testimonials"],["empty-chair-inquiries","Media Inquiries"],["empty-chair-releases","Guest Releases"],["empty-chair-form-settings","Release Form Editor"],["empty-chair-export","Export"]] },
  { label: "Marketing", testid: "admin-group-marketing", tabs: [["truth-stand","The Truth Stand"],["campaigns","Campaigns"],["templates","Email Templates"],["referrals","Referrals"],["partners","Partners"],["engagement","Polls & Q&A"],["reviews","Reviews"],["messages","Messages"]] },
  { label: "Marker Collection", testid: "admin-group-marker-collection", tabs: [["marker-collection","Marker Collection"]] },
  { label: "System", testid: "admin-group-system", tabs: [["seo-social","SEO & Social"],["navigation","Navigation"],["shipping","Shipping"],["badges","Badges"],["danger","Danger Zone"]] },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [arts, setArts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [locations, setLocations] = useState([]);
  const [qrs, setQrs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});

  /* Modal state */
  const [editing, setEditing] = useState(null);
  const [editLoc, setEditLoc] = useState(null);
  const [editPlan, setEditPlan] = useState(null);
  const [viewOrder, setViewOrder] = useState(null);
  const [viewCustomer, setViewCustomer] = useState(null);
  const [showReset, setShowReset] = useState(false);
  const [qrForm, setQrForm] = useState({ artwork_id: "", location_id: "", mode: "direct" });
  const [adminUnreadMsgs, setAdminUnreadMsgs] = useState(0);

  const reload = async () => {
    try {
      const [s, a, o, l, q, p, r, ss, cus] = await Promise.all([
        http.get("/admin/stats").then(r => r.data),
        http.get("/artworks?status_filter=").then(r => r.data),
        http.get("/orders").then(r => r.data),
        http.get("/locations").then(r => r.data),
        http.get("/qr").then(r => r.data),
        http.get("/admin/subscription-plans").then(r => r.data).catch(() => []),
        http.get("/admin/referrals").then(r => r.data).catch(() => []),
        http.get("/admin/settings").then(r => r.data).catch(() => ({})),
        http.get("/admin/customers").then(r => r.data).catch(() => []),
      ]);
      setStats(s); setArts(a); setOrders(o); setLocations(l); setQrs(q);
      setPlans(p); setReferrals(r); setSiteSettings(ss); setCustomers(cus);
    } catch (e) { /* retry on user action */ }
  };

  useEffect(() => { if (user?.role === "admin") reload(); }, [user]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    const check = () => http.get("/portal/admin/messages/unread-count").then(r => setAdminUnreadMsgs(r.data.count)).catch(() => {});
    check();
    const iv = setInterval(check, 30000);
    return () => clearInterval(iv);
  }, [user]);

  if (loading) return <Layout><div className="py-32 text-center font-ui text-[#8a857d]">Loading…</div></Layout>;
  if (!user) return <Navigate to="/login" state={{ next: "/admin" }} replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const saveSettings = async (patch) => {
    const next = { ...siteSettings, ...patch };
    setSiteSettings(next);
    await http.put("/admin/settings", patch);
    try { (await import("@/hooks/useSiteSettings")).clearSiteSettingsCache(); } catch {}
    toast.success("Saved.");
  };

  return (
    <Layout>
      <section className="bg-[#050505] pt-12 pb-24">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10">
          <div className="font-ui text-[11px] tracking-[0.32em] text-[#C9A055] mb-2">Admin</div>
          <h1 className="font-display text-3xl sm:text-5xl text-[#F4F1EA] mb-1">THE STUDIO.</h1>
          <p className="font-script text-[#C9A055] text-lg sm:text-2xl mb-6 sm:mb-10">Everything you need to manage the Collection.</p>

          {stats && (
            <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3 mb-6 sm:mb-8" data-testid="admin-stats">
              {[
                ["Orders", stats.total_orders],
                ["Paid", stats.paid_orders],
                ["Manual Pending", stats.manual_pending],
                ["Revenue", `$${stats.total_revenue}`],
                ["Artworks", stats.artworks],
                ["QR Codes", stats.qr_codes],
              ].map(([k, v]) => (
                <div key={k} className="border border-white/10 p-2.5 sm:p-4 bg-[#0a0a0a]">
                  <div className="font-ui text-[8px] sm:text-[10px] tracking-[0.32em] text-[#8a857d]">{k}</div>
                  <div className="font-display text-lg sm:text-2xl text-[#C9A055] mt-0.5 sm:mt-1">{v}</div>
                </div>
              ))}
            </div>
          )}

          {/* ═══ Promotion Mode Toggle ═══ */}
          <div className="border border-white/10 bg-[#0a0a0a] p-4 mb-6 flex items-center justify-between" data-testid="admin-hide-art-toggle">
            <div>
              <div className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055] uppercase">Promotion Mode</div>
              <div className="font-body text-xs text-[#8a857d] mt-1">
                {siteSettings.hide_art_collection
                  ? "Art Collection is HIDDEN — only Marker Collection & core pages are visible"
                  : "Art Collection is visible across the site"}
              </div>
            </div>
            <button
              onClick={() => {
                const next = !siteSettings.hide_art_collection;
                saveSettings({ hide_art_collection: next });
              }}
              className={`font-ui text-[10px] tracking-[0.22em] uppercase px-4 py-2 border transition-all ${
                siteSettings.hide_art_collection
                  ? "bg-amber-500/20 border-amber-500/40 text-amber-400 hover:bg-amber-500/30"
                  : "bg-white/5 border-white/10 text-[#8a857d] hover:border-[#C9A055] hover:text-[#C9A055]"
              }`}
              data-testid="admin-hide-art-btn"
            >
              {siteSettings.hide_art_collection ? "Art Hidden — Click to Show" : "Hide Art Collection"}
            </button>
          </div>

          <Tabs defaultValue="artworks" data-testid="admin-tabs">
            <TabsList className="bg-[#0a0a0a] border border-white/10 p-3 sm:p-5 flex flex-col gap-3 sm:gap-5 h-auto items-stretch w-full" data-testid="admin-tabs-grouped">
              {TAB_GROUPS.map((group) => (
                <div key={group.label} data-testid={group.testid} className="w-full">
                  <div className="font-ui text-[9px] tracking-[0.32em] uppercase text-[#C9A055]/70 mb-1.5 sm:mb-2">{group.label}</div>
                  <div className="flex gap-1.5 overflow-x-auto scrollbar-hide -mx-1 px-1 pb-1">
                    {group.tabs.map(([v, l]) => (
                      <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}
                        className="font-ui text-[10px] tracking-[0.22em] uppercase data-[state=active]:bg-[#C9A055] data-[state=active]:text-black border border-white/10 px-3 py-1.5 hover:border-[#C9A055] relative whitespace-nowrap flex-shrink-0">
                        {l}
                        {v === "client-mgmt" && adminUnreadMsgs > 0 && (
                          <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[8px] font-bold min-w-[16px] h-4 flex items-center justify-center rounded-full px-1" data-testid="admin-unread-badge">{adminUnreadMsgs}</span>
                        )}
                      </TabsTrigger>
                    ))}
                  </div>
                </div>
              ))}
            </TabsList>

            {/* ═══ CATALOG ═══ */}
            <TabsContent value="artworks" className="mt-8">
              <div className="flex justify-between items-center mb-6">
                <div className="font-ui-tight text-[#B5B0A7]">{arts.length} pieces</div>
                <button data-testid="new-artwork-btn" onClick={() => setEditing("new")} className="btn-gold"><Plus size={14}/> New Artwork</button>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {arts.map(a => (
                  <div key={a.id} className="border border-white/10 bg-[#0a0a0a] p-3">
                    <img src={resolveImage(a.main_image)} alt={a.title} className="w-full aspect-square object-cover mb-3"/>
                    <div className="font-display text-lg text-[#F4F1EA]">{a.title}</div>
                    <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">#{a.collection_number || "—"} · {a.status} {a.featured && "· Featured"}</div>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => setEditing(a)} className="btn-outline flex-1 justify-center text-[10px]"><Edit3 size={12}/> Edit</button>
                      <button onClick={async () => { if (confirm("Delete this piece?")) { await http.delete(`/artworks/${a.id}`); reload(); }}} className="btn-outline justify-center text-[10px] border-red-700 text-red-400"><Trash2 size={12}/></button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="pricing" className="mt-8"><PricingAdmin /></TabsContent>
            <TabsContent value="pages" className="mt-8"><PagesPanel /></TabsContent>
            <TabsContent value="content" className="mt-8"><ContentCMSTab siteSettings={siteSettings} setSiteSettings={setSiteSettings} saveSettings={saveSettings} /></TabsContent>

            {/* ═══ SALES ═══ */}
            <TabsContent value="orders" className="mt-8">
              <div className="border border-white/10 bg-[#0a0a0a]">
                <div className="grid grid-cols-6 px-4 py-3 border-b border-white/10 font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">
                  <div>Order</div><div>Email</div><div>Items</div><div>Method</div><div>Total</div><div>Status</div>
                </div>
                {orders.length === 0 ? <div className="p-6 text-[#8a857d] font-body">No orders yet.</div> : orders.map(o => (
                  <div key={o.id} onClick={() => setViewOrder(o)} className="grid grid-cols-6 px-4 py-3 border-b border-white/5 items-center text-sm cursor-pointer hover:bg-white/5" data-testid={`order-row-${o.order_number}`}>
                    <div className="font-mono text-[#F4F1EA] text-xs">{o.order_number}</div>
                    <div className="text-[#B5B0A7] truncate">{o.email}</div>
                    <div className="text-[#B5B0A7]">{(o.items || []).length}</div>
                    <div className="text-[#B5B0A7] capitalize">{o.payment_method}</div>
                    <div className="text-[#C9A055]">${(o.total || 0).toFixed(2)}</div>
                    <div><span className={`font-ui text-[10px] tracking-[0.18em] uppercase ${o.payment_status === "paid" ? "text-emerald-400" : o.payment_status === "manual_pending" ? "text-amber-400" : "text-[#B5B0A7]"}`}>{o.payment_status}</span></div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="customers" className="mt-8">
              <div className="border border-white/10 bg-[#0a0a0a]">
                <div className="grid grid-cols-7 px-4 py-3 border-b border-white/10 font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">
                  <div className="col-span-2">Name</div><div className="col-span-2">Email</div><div>Orders</div><div>LTV</div><div>Credit</div>
                </div>
                {customers.length === 0 ? <div className="p-6 text-[#8a857d] font-body">No customers yet.</div> : customers.map(u => (
                  <div key={u.id} onClick={async () => { const { data } = await http.get(`/admin/customers/${u.id}`); setViewCustomer(data); }} className="grid grid-cols-7 px-4 py-3 border-b border-white/5 items-center text-sm cursor-pointer hover:bg-white/5" data-testid={`customer-row-${u.email}`}>
                    <div className="col-span-2 text-[#F4F1EA]">{u.name || "—"} <span className="font-ui text-[9px] tracking-[0.22em] text-[#8a857d]">{u.role !== "customer" && `· ${u.role}`}</span></div>
                    <div className="col-span-2 text-[#B5B0A7] truncate">{u.email}</div>
                    <div className="text-[#B5B0A7]">{u.paid_orders} / {u.total_orders}</div>
                    <div className="text-[#C9A055]">${(u.lifetime_value || 0).toFixed(2)}</div>
                    <div className="text-[#C9A055]">${(u.referral_credit || 0).toFixed(2)}</div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="gift-cards" className="mt-8"><GiftCardsAdmin /></TabsContent>
            <TabsContent value="subscriptions" className="mt-8"><SubscriptionsPanel plans={plans} reload={reload}/></TabsContent>

            <TabsContent value="plans" className="mt-8">
              <div className="flex justify-between items-center mb-6">
                <div className="font-ui-tight text-[#B5B0A7]">{plans.length} plans</div>
                <button onClick={() => setEditPlan({ name: "", description: "", frequency: "monthly", price: 19, perks: [], active: true, featured: false, sort_order: plans.length + 1 })} className="btn-gold" data-testid="new-plan-btn"><Plus size={14}/> New Plan</button>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {plans.map(p => (
                  <div key={p.id} className={`border bg-[#0a0a0a] p-5 ${p.featured ? "border-[#C9A055]" : "border-white/10"}`}>
                    <div className="font-display text-2xl text-[#F4F1EA]">{p.name.toUpperCase()}</div>
                    <div className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055]">{p.frequency} · ${p.price}{p.active ? "" : " · INACTIVE"}{p.featured ? " · FEATURED" : ""}</div>
                    <p className="font-body text-sm text-[#B5B0A7] mt-2">{p.description}</p>
                    <div className="flex gap-2 mt-4">
                      <button onClick={() => setEditPlan(p)} className="btn-outline text-[10px]"><Edit3 size={12}/> Edit</button>
                      <button onClick={async () => { if (confirm("Delete?")) { await http.delete(`/admin/subscription-plans/${p.id}`); reload(); }}} className="btn-outline text-[10px] border-red-700 text-red-400"><Trash2 size={12}/></button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="support" className="mt-8"><AdminSupport /></TabsContent>

            {/* ═══ TRUTH REGISTRY ═══ */}
            <TabsContent value="truths" className="mt-8"><AdminTruths /></TabsContent>
            <TabsContent value="registry-admin" className="mt-8"><AdminRegistry /></TabsContent>
            <TabsContent value="releases" className="mt-8"><AdminTruthReleases /></TabsContent>
            <TabsContent value="interpretations" className="mt-8"><AdminInterpretations /></TabsContent>
            <TabsContent value="truth-wall" className="mt-8"><AdminTruthWall /></TabsContent>
            <TabsContent value="truth-markers" className="mt-8"><AdminTruthMarkers /></TabsContent>
            <TabsContent value="truth-cards" className="mt-8"><AdminTruthCards /></TabsContent>
            <TabsContent value="truth-print" className="mt-8"><AdminTruthPrint /></TabsContent>
            <TabsContent value="offsite-sale" className="mt-8"><AdminOffSiteSale /></TabsContent>

            <TabsContent value="qrcodes" className="mt-8">
              <div className="border border-white/10 bg-[#0a0a0a] p-5 mb-6">
                <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                  <div><div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">Assign Artwork to a Location</div></div>
                  {qrs.length > 0 && (
                    <a href={`${API_BASE}/api/admin/qr/labels/pdf`}
                      onClick={(e) => {
                        e.preventDefault();
                        const tok = localStorage.getItem("stc_token");
                        toast("Building bulk flyer PDF — give it a few seconds…", { duration: 4000 });
                        fetch(`${API_BASE}/api/admin/qr/labels/pdf?base_url=${encodeURIComponent(window.location.origin)}`, {
                          headers: { Authorization: `Bearer ${tok || ""}` }
                        })
                          .then(r => { if (!r.ok) throw new Error("Failed"); return r.blob(); })
                          .then(blob => {
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url; a.download = `stc-qr-flyers-${new Date().toISOString().slice(0,10)}.pdf`;
                            document.body.appendChild(a); a.click(); a.remove();
                            URL.revokeObjectURL(url);
                            toast.success(`Downloaded ${qrs.length} flyer${qrs.length === 1 ? "" : "s"}.`);
                          })
                          .catch(() => toast.error("Could not build the PDF. Check the logs."));
                      }}
                      data-testid="bulk-print-pdf-btn"
                      className="inline-flex items-center gap-2 px-4 py-2 border border-[#C9A055] bg-[#C9A055]/10 text-[#C9A055] hover:bg-[#C9A055] hover:text-black font-ui text-[10px] tracking-[0.32em] uppercase transition-colors">
                      <Download size={13} /> Bulk Print · {qrs.length} flyer{qrs.length === 1 ? "" : "s"}
                    </a>
                  )}
                </div>
                <div className="grid sm:grid-cols-4 gap-3">
                  <select className="stc-input" value={qrForm.artwork_id} onChange={e => setQrForm({...qrForm, artwork_id: e.target.value})}>
                    <option value="">Pick an artwork…</option>
                    {arts.map(a => <option key={a.id} value={a.id}>{a.title}</option>)}
                  </select>
                  <select className="stc-input" value={qrForm.location_id} onChange={e => setQrForm({...qrForm, location_id: e.target.value})}>
                    <option value="">Pick a location…</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.business_name} — {l.location_name}</option>)}
                  </select>
                  <select className="stc-input" value={qrForm.mode} onChange={e => setQrForm({...qrForm, mode: e.target.value})} data-testid="qr-mode-select">
                    <option value="direct">Direct → Buy Now</option>
                    <option value="story">Story → Read First</option>
                  </select>
                  <button disabled={!qrForm.artwork_id || !qrForm.location_id} onClick={async () => { await http.post("/qr", qrForm); setQrForm({ artwork_id: "", location_id: "", mode: "direct" }); reload(); toast.success("QR generated."); }} className="btn-gold" data-testid="qr-generate-btn"><QrCode size={14}/> Generate</button>
                </div>
                <p className="font-body text-[12px] text-[#8a857d] mt-3">
                  <span className="text-[#C9A055]">Direct</span> jumps straight to the artwork page. <span className="text-[#C9A055]">Story</span> lands on an immersive story-first page — great for partner cafés &amp; bars.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {qrs.map(q => {
                  const origin = window.location.origin;
                  const pngUrl = `${API_BASE}/api/qr/png/${q.qr_code}?base_url=${encodeURIComponent(origin)}`;
                  return (
                    <div key={q.id} className="border border-white/10 bg-[#0a0a0a] p-4">
                      <div className="bg-white p-3 mb-3 inline-block"><img src={pngUrl} alt="qr" className="w-32 h-32"/></div>
                      <div className="font-display text-lg text-[#F4F1EA] truncate">{q.artwork?.title || "—"}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055]">{q.location?.business_name || "—"}</span>
                        <span className={`font-ui text-[9px] tracking-[0.22em] uppercase px-1.5 py-0.5 border ${q.mode === "story" ? "border-[#C9A055] text-[#C9A055]" : "border-white/20 text-[#B5B0A7]"}`} data-testid={`qr-mode-badge-${q.qr_code}`}>{q.mode === "story" ? "Story" : "Direct"}</span>
                      </div>
                      <div className="flex justify-between mt-2 font-ui text-[10px] tracking-[0.22em] text-[#B5B0A7]"><span>Scans: {q.scan_count || 0}</span><span>Sales: {q.purchase_count || 0}</span></div>
                      <div className="flex gap-2 mt-3">
                        <a href={pngUrl} download={`qr-${q.qr_code}.png`} className="btn-outline text-[10px]" data-testid={`qr-png-${q.qr_code}`}><Download size={12}/> PNG</a>
                        <a href={`${API_BASE}/api/qr/label/${q.qr_code}?base_url=${encodeURIComponent(origin)}`} download={`stc-label-${q.qr_code}.png`} className="btn-outline text-[10px] border-[#C9A055] text-[#C9A055]" data-testid={`qr-label-${q.qr_code}`}><Download size={12}/> Print Label</a>
                        <button onClick={async () => { if (confirm("Delete?")) { await http.delete(`/qr/${q.id}`); reload(); }}} className="btn-outline text-[10px] border-red-700 text-red-400"><Trash2 size={12}/></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </TabsContent>

            <TabsContent value="locations" className="mt-8">
              <div className="flex justify-between items-center mb-6">
                <div className="font-ui-tight text-[#B5B0A7]">{locations.length} locations</div>
                <button onClick={() => setEditLoc({ business_name: "", location_name: "", address: "", contact_person: "", contact_email: "", notes: "", active: true })} className="btn-gold"><Plus size={14}/> New Location</button>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {locations.map(l => (
                  <div key={l.id} className="border border-white/10 bg-[#0a0a0a] p-4">
                    <div className="font-display text-lg text-[#F4F1EA]">{l.business_name}</div>
                    <div className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055]">{l.location_name}</div>
                    <div className="font-body text-sm text-[#B5B0A7] mt-2">{l.address}</div>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => setEditLoc(l)} className="btn-outline text-[10px]"><Edit3 size={12}/> Edit</button>
                      <button onClick={async () => { if (confirm("Delete?")) { await http.delete(`/locations/${l.id}`); reload(); }}} className="btn-outline text-[10px] border-red-700 text-red-400"><Trash2 size={12}/></button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* ═══ MIRROR CONVERSATIONS ═══ */}
            <TabsContent value="mirror" className="mt-8"><AdminMirrorConversations /></TabsContent>
            <TabsContent value="mirror-contacts" className="mt-8"><AdminMirrorLeads /></TabsContent>
            <TabsContent value="reflection-library" className="mt-8"><AdminReflectionLibrary /></TabsContent>
            <TabsContent value="booking" className="mt-8"><AdminBooking /></TabsContent>
            <TabsContent value="forms" className="mt-8"><AdminForms /></TabsContent>
            <TabsContent value="client-mgmt" className="mt-8"><AdminClientMgmt /></TabsContent>
            <TabsContent value="breathing-room" className="mt-8"><AdminBreathingRoom /></TabsContent>
            <TabsContent value="workshops-admin" className="mt-8"><AdminWorkshops /></TabsContent>

            {/* ═══ THE EMPTY CHAIR ═══ */}
            <TabsContent value="empty-chair" className="mt-8"><AdminEmptyChairSetup /></TabsContent>
            <TabsContent value="empty-chair-episodes" className="mt-8"><AdminEmptyChairEpisodes /></TabsContent>
            <TabsContent value="empty-chair-requests" className="mt-8"><AdminEmptyChairRequests /></TabsContent>
            <TabsContent value="empty-chair-faq" className="mt-8"><AdminEmptyChairFAQ /></TabsContent>
            <TabsContent value="empty-chair-testimonials" className="mt-8"><AdminEmptyChairTestimonials /></TabsContent>
            <TabsContent value="empty-chair-inquiries" className="mt-8"><AdminEmptyChairInquiries /></TabsContent>
            <TabsContent value="empty-chair-releases" className="mt-8"><AdminGuestReleases /></TabsContent>
            <TabsContent value="empty-chair-form-settings" className="mt-8"><GuestReleaseFormSettings /></TabsContent>
            <TabsContent value="empty-chair-export" className="mt-8"><AdminEmptyChairExport /></TabsContent>

            {/* ═══ MARKETING ═══ */}
            <TabsContent value="truth-stand"><AdminTruthStand /></TabsContent>
            <TabsContent value="campaigns" className="mt-8"><CampaignsAdmin /></TabsContent>
            <TabsContent value="templates" className="mt-8"><EmailTemplatesAdmin /></TabsContent>

            <TabsContent value="referrals" className="mt-8">
              <div className="border border-white/10 bg-[#0a0a0a] p-5 mb-6">
                <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3">Referral Reward Amount</div>
                <div className="flex gap-3 items-center">
                  <input type="number" step="0.5" value={siteSettings.referral_reward_amount ?? 10} onChange={e => setSiteSettings({...siteSettings, referral_reward_amount: parseFloat(e.target.value)})} className="stc-input max-w-[160px]"/>
                  <button onClick={() => saveSettings({ referral_reward_amount: siteSettings.referral_reward_amount })} className="btn-gold">Save</button>
                  <span className="font-body text-sm text-[#B5B0A7]">USD credit per qualified referral</span>
                </div>
              </div>
              <div className="border border-white/10 bg-[#0a0a0a]">
                <div className="grid grid-cols-5 px-4 py-3 border-b border-white/10 font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">
                  <div>Referrer</div><div>Code</div><div>Referred</div><div>Status</div><div>Reward</div>
                </div>
                {referrals.length === 0 ? <div className="p-6 text-[#8a857d] font-body">No referrals yet.</div> : referrals.map(r => (
                  <div key={r.id} className="grid grid-cols-5 px-4 py-3 border-b border-white/5 items-center text-sm">
                    <div className="text-[#F4F1EA]">{r.referrer?.name || r.referrer?.email}</div>
                    <div className="font-mono text-[#C9A055] text-xs">{r.referrer?.referral_code}</div>
                    <div className="text-[#B5B0A7]">{r.referred_email}</div>
                    <div><span className={`font-ui text-[10px] tracking-[0.18em] uppercase ${r.status === "qualified" ? "text-emerald-400" : "text-amber-400"}`}>{r.status}</span></div>
                    <div className="text-[#C9A055]">${(r.reward_amount || 0).toFixed(2)}</div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="partners" className="mt-8 space-y-8"><AdminPartnerProgram /></TabsContent>
            <TabsContent value="engagement" className="mt-8"><AdminEngagement /></TabsContent>
            <TabsContent value="reviews" className="mt-8"><AdminReviews /></TabsContent>
            <TabsContent value="messages" className="mt-8"><MessagesPanel /></TabsContent>

            {/* ═══ MARKER COLLECTION ═══ */}
            <TabsContent value="marker-collection" className="mt-8"><AdminMarkerCollection /></TabsContent>

            {/* ═══ SYSTEM ═══ */}
            <TabsContent value="seo-social" className="mt-8"><AdminSEO /></TabsContent>
            <TabsContent value="navigation" className="mt-8"><AdminNavManager /></TabsContent>
            <TabsContent value="shipping" className="mt-8"><ShippingAdmin /></TabsContent>
            <TabsContent value="badges" className="mt-8"><AdminBadges /></TabsContent>

            <TabsContent value="danger" className="mt-8">
              <div className="border border-red-700/40 bg-red-900/10 p-6">
                <div className="font-ui text-[10px] tracking-[0.32em] text-red-400 mb-2">Danger Zone</div>
                <h3 className="font-display text-2xl text-[#F4F1EA] mb-2">RESET TEST DATA</h3>
                <p className="font-body text-[#B5B0A7] mb-5">Wipe fake orders, test customers, partner applications, and other dev artifacts. Pick what to clear — your artwork catalog and admin account are always preserved.</p>
                <button data-testid="open-reset-modal" onClick={() => setShowReset(true)} className="btn-outline border-red-600 text-red-400 hover:text-red-300 hover:border-red-400">Open Reset Tool</button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {editing && <ArtworkModal art={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }}/>}
      {editLoc && <LocationModal loc={editLoc} onClose={() => setEditLoc(null)} onSaved={() => { setEditLoc(null); reload(); }}/>}
      {editPlan && <PlanModal plan={editPlan} onClose={() => setEditPlan(null)} onSaved={() => { setEditPlan(null); reload(); }}/>}
      {viewOrder && <OrderModal order={viewOrder} onClose={() => setViewOrder(null)} onSaved={() => { setViewOrder(null); reload(); }}/>}
      {viewCustomer && <CustomerModal data={viewCustomer} onClose={() => setViewCustomer(null)} onOpenOrder={(o) => { setViewCustomer(null); setViewOrder(o); }}/>}
      {showReset && <ResetModal onClose={() => setShowReset(false)} onDone={() => { setShowReset(false); reload(); }}/>}
    </Layout>
  );
}
